import { Bookmark as BookmarkIcon, Trash2, Edit2, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { Bookmark, NavigationCallbacks } from '../types';
import styles from '../ReaderPanels.module.css';
import { useAlert } from '../../../contexts/AlertContext';
import { useTranslation } from '../../../i18n/useTranslation';
import {
  primaryButtonStyle,
  secondaryButtonStyle,
  iconButtonStyle,
  compactTextareaStyle,
  buttonRowStyle,
} from './controls';

interface BookmarksPanelProps {
  bookmarks: Bookmark[];
  onNavigate: NavigationCallbacks['onNavigateToBookmark'];
  onDelete: (bookmarkId: string) => Promise<void>;
  onUpdate: (bookmarkId: string, note: string) => Promise<void>;
  onCreateBookmark: (location: string | number, note?: string) => Promise<void>;
  currentLocation: string | number;
  currentLocationLabel: string;
}

/**
 * BookmarksPanel
 *
 * Displays user bookmarks with edit/delete functionality.
 * Uses CSS modules for modern, accessible styling.
 */
export default function BookmarksPanel({
  bookmarks,
  onNavigate,
  onDelete,
  onUpdate,
  onCreateBookmark,
  currentLocation,
  currentLocationLabel,
}: BookmarksPanelProps) {
  const { t } = useTranslation();
  const { showToast, confirm } = useAlert();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNote, setEditNote] = useState('');
  const [isAddingBookmark, setIsAddingBookmark] = useState(false);
  const [addNote, setAddNote] = useState('');
  const [isSavingAdd, setIsSavingAdd] = useState(false);
  const addCardRef = useRef<HTMLDivElement>(null);
  const addTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isAddingBookmark) {
      addCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      addTextareaRef.current?.focus();
    }
  }, [isAddingBookmark]);

  const handleEdit = (bookmark: Bookmark) => {
    setEditingId(bookmark.id);
    setEditNote(bookmark.note || '');
  };

  const handleSaveEdit = async (bookmarkId: string) => {
    try {
      await onUpdate(bookmarkId, editNote);
      setEditingId(null);
      setEditNote('');
    } catch (error: any) {
      console.error('Failed to update bookmark:', error);
      const backendMessage =
        error?.response?.data?.error?.message ||
        error?.message ||
        'Failed to update bookmark. Please try again.';
      showToast(backendMessage || t('App.Reader.Bookmarks.UpdateError'), { type: 'error' });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditNote('');
  };

  const handleDelete = async (bookmarkId: string) => {
    const ok = await confirm({
      title: t('App.Reader.Bookmarks.DeleteConfirmTitle'),
      message: t('App.Reader.Bookmarks.DeleteConfirmMessage'),
      confirmText: t('App.Reader.Bookmarks.DeleteConfirmButton'),
      variant: 'danger',
    });
    if (ok) {
      try {
        await onDelete(bookmarkId);
      } catch (error) {
        console.error('Failed to delete bookmark:', error);
        showToast(t('App.Reader.Bookmarks.DeleteError'), { type: 'error' });
      }
    }
  };

  const handleStartAddBookmark = () => {
    setIsAddingBookmark(true);
    setAddNote('');
  };

  const handleSaveAddBookmark = async () => {
    setIsSavingAdd(true);
    try {
      await onCreateBookmark(currentLocation, addNote.trim() || undefined);
      setIsAddingBookmark(false);
      setAddNote('');
    } catch (error: any) {
      console.error('Failed to create bookmark:', error);
      // Prefer the backend's UserFriendlyException message (ABP error shape)
      const backendMessage =
        error?.response?.data?.error?.message ||
        error?.message ||
        t('App.Reader.Bookmarks.CreateError');
      showToast(backendMessage, { type: 'error' });
    } finally {
      setIsSavingAdd(false);
    }
  };

  const handleCancelAddBookmark = () => {
    setIsAddingBookmark(false);
    setAddNote('');
  };

  const sortedBookmarks = [...bookmarks].sort((a, b) => {
    // Sort by location (page number or other)
    const locA = typeof a.location === 'number' ? a.location : 0;
    const locB = typeof b.location === 'number' ? b.location : 0;
    return locA - locB;
  });

  return (
    <div className={styles.panelContainer} style={{ width: '100%', padding: '20px' }}>
      {/* Header: count + Add action (matches NotesPanel / FlashcardPanel) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--spacing-3)',
          gap: '8px',
        }}
      >
        <span style={{ fontSize: '0.813rem', color: 'var(--color-text-secondary)' }}>
          {t('App.Reader.Bookmarks.Count', { count: bookmarks.length })}
        </span>
        <button
          onClick={handleStartAddBookmark}
          disabled={isAddingBookmark}
          style={{
            ...primaryButtonStyle,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            opacity: isAddingBookmark ? 0.6 : 1,
            cursor: isAddingBookmark ? 'not-allowed' : 'pointer',
          }}
          aria-label={t('App.Reader.Bookmarks.AddAriaLabel')}
        >
          <Plus style={{ width: '14px', height: '14px' }} />
          {t('App.Reader.Bookmarks.Add')}
        </button>
      </div>

      {/* Bookmarks List */}
      <div className={styles.panelBody} style={{ padding: 0 }}>
        {/* Inline Add Bookmark Card — mirrors the edit-mode layout */}
        {isAddingBookmark && (
          <div
            ref={addCardRef}
            className={styles.noteCard}
            style={{
              padding: '10px 12px',
              marginBottom: '8px',
              borderLeft: '4px solid rgb(var(--color-primary-500))',
              borderRadius: '6px',
              background: 'var(--color-surface)',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
            }}
          >
            <div className={styles.noteCardHeader} style={{ margin: 0, gap: '8px' }}>
              <div style={{ flex: 1, minWidth: 0, textAlign: 'left', padding: 0, background: 'transparent', border: 'none' }}>
                <div
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    marginBottom: '4px',
                  }}
                >
                  {currentLocationLabel}
                </div>

                <div style={{ marginTop: '6px' }}>
                  <textarea
                    ref={addTextareaRef}
                    value={addNote}
                    onChange={(e) => setAddNote(e.target.value)}
                    rows={2}
                    placeholder={t('App.Reader.Bookmarks.NoteEditPlaceholder')}
                    aria-label={t('App.Reader.Bookmarks.NoteAriaLabel')}
                    style={compactTextareaStyle}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        handleCancelAddBookmark();
                      }
                    }}
                  />
                  <div style={buttonRowStyle}>
                    <button
                      onClick={handleSaveAddBookmark}
                      disabled={isSavingAdd}
                      aria-label={t('App.Reader.Bookmarks.SaveAriaLabel')}
                      style={{
                        ...primaryButtonStyle,
                        opacity: isSavingAdd ? 0.6 : 1,
                        cursor: isSavingAdd ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {isSavingAdd ? t('App.Reader.Bookmarks.Saving') : t('App.Reader.Bookmarks.Save')}
                    </button>
                    <button
                      onClick={handleCancelAddBookmark}
                      disabled={isSavingAdd}
                      aria-label={t('App.Reader.Bookmarks.CancelAddAriaLabel')}
                      style={secondaryButtonStyle}
                    >
                      {t('App.Reader.Bookmarks.Cancel')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {sortedBookmarks.length === 0 && !isAddingBookmark && (
          <div className={styles.emptyState}>
            <BookmarkIcon className={styles.emptyStateIcon} />
            <h3 className={styles.emptyStateTitle}>{t('App.Reader.Bookmarks.EmptyTitle')}</h3>
            <p className={styles.emptyStateDescription}>
              {t('App.Reader.Bookmarks.EmptyDescription')}
            </p>
          </div>
        )}

        {sortedBookmarks.map((bookmark) => (
          <div
            key={bookmark.id}
            className={styles.noteCard}
            style={{
              padding: '10px 12px',
              marginBottom: '8px',
              borderLeft: '4px solid rgb(var(--color-primary-500))',
              borderRadius: '6px',
              background: 'var(--color-surface)',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
            }}
          >
            <div
              className={styles.noteCardHeader}
              style={{ margin: 0, alignItems: 'center', gap: '8px' }}
            >
              <div style={{ flex: 1, minWidth: 0, textAlign: 'left', padding: 0, background: 'transparent', border: 'none' }}>
                <button
                  type="button"
                  onClick={() => editingId !== bookmark.id && onNavigate(bookmark)}
                  style={{
                    cursor: editingId !== bookmark.id ? 'pointer' : 'default',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: 'var(--color-text-primary)',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  {bookmark.locationLabel}
                </button>
                {editingId === bookmark.id ? (
                  <div style={{ marginTop: '6px' }}>
                    <textarea
                      value={editNote}
                      onChange={(e) => setEditNote(e.target.value)}
                      rows={2}
                      placeholder={t('App.Reader.Bookmarks.NoteEditPlaceholder')}
                      autoFocus
                      aria-label={t('App.Reader.Bookmarks.NoteAriaLabel')}
                      style={compactTextareaStyle}
                    />
                    <div style={buttonRowStyle}>
                      <button
                        onClick={() => handleSaveEdit(bookmark.id)}
                        aria-label={t('App.Reader.Bookmarks.SaveAriaLabel')}
                        style={primaryButtonStyle}
                      >
                        {t('App.Reader.Bookmarks.Save')}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        aria-label={t('App.Reader.Bookmarks.Cancel')}
                        style={secondaryButtonStyle}
                      >
                        {t('App.Reader.Bookmarks.Cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {bookmark.note && (
                      <div
                        style={{
                          marginTop: '4px',
                          fontSize: '0.813rem',
                          color: 'var(--color-text-secondary)',
                          lineHeight: 1.4,
                        }}
                      >
                        {bookmark.note}
                      </div>
                    )}
                    <div
                      style={{
                        marginTop: '2px',
                        fontSize: '0.688rem',
                        color: 'var(--color-text-tertiary)',
                      }}
                    >
                      {new Date(bookmark.createdAt).toLocaleDateString()}
                    </div>
                  </>
                )}
              </div>

              {editingId !== bookmark.id && (
                <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                  <button
                    onClick={() => handleEdit(bookmark)}
                    aria-label={t('App.Reader.Bookmarks.EditAriaLabel')}
                    title={t('App.Reader.Bookmarks.EditTooltip')}
                    style={iconButtonStyle}
                  >
                    <Edit2 style={{ width: '14px', height: '14px' }} />
                  </button>
                  <button
                    onClick={() => handleDelete(bookmark.id)}
                    aria-label={t('App.Reader.Bookmarks.DeleteAriaLabel')}
                    title={t('App.Reader.Bookmarks.DeleteAriaLabel')}
                    style={iconButtonStyle}
                  >
                    <Trash2 style={{ width: '14px', height: '14px' }} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
